import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { verificationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const PhotoVerification = () => {
  const [photos, setPhotos] = useState({ profilePhotos: [], galleryPhotos: [] });
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async (status = null) => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const response = await verificationAPI.getPendingPhotos(params);
      let data = response.data.data;
      
      // Sort photos based on sortOrder
      if (sortOrder === 'asc') {
        data.profilePhotos = data.profilePhotos?.sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        ) || [];
        data.galleryPhotos = data.galleryPhotos?.sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        ) || [];
      } else {
        data.profilePhotos = data.profilePhotos?.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ) || [];
        data.galleryPhotos = data.galleryPhotos?.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ) || [];
      }
      
      setPhotos(data);
    } catch (error) {
      toast.error('Failed to fetch photos');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statuses = [null, 'pending', 'approved', 'rejected'];
    fetchPhotos(statuses[newValue]);
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
    const statuses = [null, 'pending', 'approved', 'rejected'];
    fetchPhotos(statuses[tabValue]);
  };

  const handleApprove = async (type, id) => {
    try {
      await verificationAPI.approvePhoto(type, id);
      toast.success('Photo approved successfully');
      const statuses = [null, 'pending', 'approved', 'rejected'];
      fetchPhotos(statuses[tabValue]);
    } catch (error) {
      toast.error('Failed to approve photo');
    }
  };

  const handleReject = async (type, id) => {
    try {
      await verificationAPI.rejectPhoto(type, id);
      toast.success('Photo rejected and removed');
      const statuses = [null, 'pending', 'approved', 'rejected'];
      fetchPhotos(statuses[tabValue]);
    } catch (error) {
      toast.error('Failed to reject photo');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 1: return 'success';
      case 2: return 'error';
      default: return 'warning';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 1: return 'Approved';
      case 2: return 'Rejected';
      default: return 'Pending';
    }
  };

  const getStatusCount = (status) => {
    const allPhotos = [...photos.profilePhotos, ...photos.galleryPhotos];
    if (status === 'all') return allPhotos.length;
    const statusMap = { pending: 0, approved: 1, rejected: 2 };
    return allPhotos.filter(p => p.status === statusMap[status]).length;
  };

  const PhotoCard = ({ photo, type, title }) => {
    const imageUrl = type === 'profile' ? photo.profile_photo_url : photo.image_url;
    const statusInfo = {
      label: getStatusLabel(photo.status),
      color: getStatusColor(photo.status)
    };

    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Box>
              <Typography variant="h6">
                {photo.first_name} {photo.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                User ID: {photo.user_id} | Phone: {photo.phone}
              </Typography>
            </Box>
            <Chip 
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
            />
          </Box>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Submitted: {moment(photo.created_at).fromNow()}
          </Typography>

          <Box sx={{ mt: 2, position: 'relative' }}>
            <img 
              src={imageUrl}
              alt="Photo"
              style={{ 
                width: '100%', 
                height: 200, 
                objectFit: 'cover', 
                borderRadius: 8,
                cursor: 'pointer'
              }}
              onClick={() => {
                setPreviewUrl(imageUrl);
                setPreviewOpen(true);
              }}
            />
          </Box>

          {photo.status === 0 && (
            <Box display="flex" gap={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                fullWidth
                onClick={() => handleApprove(type, photo.id)}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                fullWidth
                onClick={() => handleReject(type, photo.id)}
              >
                Reject
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Photo Verification
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              value={sortOrder}
              label="Sort"
              onChange={handleSortChange}
            >
              <MenuItem value="desc">Latest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => {
              const statuses = [null, 'pending', 'approved', 'rejected'];
              fetchPhotos(statuses[tabValue]);
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Status Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('all')} color="primary">
                All
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('pending')} color="warning">
                Pending
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('approved')} color="success">
                Approved
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={getStatusCount('rejected')} color="error">
                Rejected
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {/* Profile Photos */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Profile Photos ({photos.profilePhotos?.length || 0})
          </Typography>
          <Grid container spacing={3}>
            {photos.profilePhotos?.map((photo) => (
              <Grid item xs={12} md={6} lg={4} key={`profile-${photo.id}`}>
                <PhotoCard photo={photo} type="profile" title="Profile Photo" />
              </Grid>
            ))}
            {photos.profilePhotos?.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No profile photos found</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Gallery Photos */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Gallery Photos ({photos.galleryPhotos?.length || 0})
          </Typography>
          <Grid container spacing={3}>
            {photos.galleryPhotos?.map((photo) => (
              <Grid item xs={12} md={6} lg={4} key={`gallery-${photo.id}`}>
                <PhotoCard photo={photo} type="gallery" title="Gallery Photo" />
              </Grid>
            ))}
            {photos.galleryPhotos?.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No gallery photos found</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <img src={previewUrl} alt="Preview" style={{ width: '100%' }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoVerification;